import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  HiFolder,
  HiUserGroup,
  HiAcademicCap,
  HiRocketLaunch,
  HiChartBar,
  HiHeart,
} from 'react-icons/hi2';
import Footer from '../../components/ui/Footer';
import MyHeader from '../../components/ui/MyHeader';
import RegistrationPage from '../auth/RegistrationPage';
import LoginScreen from '../auth/LoginScreen';
import StartApplicationButton from '../../components/ui/StartApplicationButton';
import StatsSection from '../../components/ui/StatsSection';

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const LandingPage = () => {
  const aboutRef = useRef(null);
  const programsRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  const scrollTo = ref => ref.current?.scrollIntoView({ behavior: 'smooth' });

  const handleApplyNow = () => setModalOpen(true);
  const handleCloseModal = () => setModalOpen(false);
  const handleLogin = () => {
    setLoginModalOpen(true);
  };
  const handleCloseLoginModal = () => {
    setLoginModalOpen(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-green-100 transition-all duration-300">
      <MyHeader onApplyNow={handleApplyNow} onLogin={handleLogin} />
      <RegistrationPage open={modalOpen} onClose={handleCloseModal} />
      <LoginScreen open={loginModalOpen} onClose={handleCloseLoginModal} />
      <section ref={aboutRef} className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-5xl md:text-4xl">
            Empowering{' '}
            <span className="bg-gradient-to-r from-blue-600 via-teal-500 to-green-500 bg-clip-text text-transparent">
              Entrepreneurs
            </span>
            <br />
            <span className="text-gray-600">From Idea to Impact</span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-gray-600 md:text-lg">
            Join our comprehensive Entrepreneurship Development Program. Get training, coaching,
            funding, and ongoing support to build and scale your business successfully.
          </p>
        </motion.div>

        <div className="mt-8 flex  justify-center gap-4 md:mt-10">
          <StartApplicationButton onApplyNow={handleApplyNow} />
        </div>

        <div className="mt-4 md:mt-5">
          <StatsSection />
        </div>

        <div className="mt-12 px-4 text-center md:mt-16">
          <h2 className="text-3xl font-extrabold tracking-tight text-gray-900 sm:text-5xl md:text-4xl">
            <span className="bg-gradient-to-r from-blue-600 via-teal-500 to-green-500 bg-clip-text text-transparent">
              Entrepreneur
            </span>{' '}
            <span className="text-black">Management</span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-gray-600 md:text-lg">
            Our Client Management System spans the entire entrepreneurial journey, from initial
            application to alumni success.
          </p>
        </div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mt-12 grid gap-6 md:mt-16 md:grid-cols-3 md:gap-8"
        >
          <motion.div
            variants={cardVariants}
            className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-white to-blue-50 p-6 shadow-sm transition-all hover:scale-[1.02] hover:shadow-lg md:p-8"
          >
            <div className="mb-3 flex justify-center text-3xl md:text-4xl">
              <HiFolder className="h-10 w-10 text-blue-500" />
            </div>
            <div className="flex justify-center">
              <div className="mb-3 inline-block rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-600 md:px-3 md:text-sm">
                Pre-Admission
              </div>
            </div>
            <h3 className="mb-3 text-center text-lg font-semibold text-gray-900 md:text-xl">
              Applicant Platform
            </h3>
            <p className="mb-3 text-sm text-gray-600 md:mb-4 md:text-base">
              Streamlined application process for prospective entrepreneurs.
            </p>
            <ul className="space-y-1.5 text-sm text-gray-600 md:space-y-2 md:text-base">
              <li className="flex items-center">
                <span className="mr-2">•</span>
                Online application with document upload
              </li>
              <li className="flex items-center">
                <span className="mr-2">•</span>
                Auto generated application ID
              </li>
              <li className="flex items-center">
                <span className="mr-2">•</span>
                Application status tracking
              </li>
              <li className="flex items-center">
                <span className="mr-2">•</span>
                Interview scheduling system
              </li>
            </ul>
          </motion.div>

          <motion.div
            variants={cardVariants}
            className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-white to-green-50 p-6 shadow-sm transition-all hover:scale-[1.02] hover:shadow-lg md:p-8"
          >
            <div className="mb-3 flex justify-center text-3xl md:text-4xl">
              <HiUserGroup className="h-10 w-10 text-green-500" />
            </div>
            <div className="flex justify-center">
              <div className="mb-3 inline-block rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-600 md:px-3 md:text-sm">
                Active Participants
              </div>
            </div>
            <h3 className="mb-3 text-center text-lg font-semibold text-gray-900 md:text-xl">
              Client Platform
            </h3>
            <p className="mb-3 text-sm text-gray-600 md:mb-4 md:text-base">
              Comprehensive control hub for active program participants
            </p>
            <ul className="space-y-1.5 text-sm text-gray-600 md:space-y-2 md:text-base">
              <li className="flex items-center">
                <span className="mr-2">•</span>
                Personalized training dashboard
              </li>
              <li className="flex items-center">
                <span className="mr-2">•</span>
                Coach assignment & communication
              </li>
              <li className="flex items-center">
                <span className="mr-2">•</span>
                Progress tracking & milestones
              </li>
              <li className="flex items-center">
                <span className="mr-2">•</span>
                Resource library access
              </li>
            </ul>
          </motion.div>

          <motion.div
            variants={cardVariants}
            className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-white to-purple-50 p-6 shadow-sm transition-all hover:scale-[1.02] hover:shadow-lg md:p-8"
          >
            <div className="mb-3 flex justify-center text-3xl md:text-4xl">
              <HiAcademicCap className="h-10 w-10 text-purple-500" />
            </div>
            <div className="flex justify-center">
              <div className="mb-3 inline-block rounded-full bg-purple-100 px-2 py-1 text-xs font-medium text-purple-600 md:px-3 md:text-sm">
                Post-Program
              </div>
            </div>
            <h3 className="mb-3 text-center text-lg font-semibold text-gray-900 md:text-xl">
              Alumni Platform
            </h3>
            <p className="mb-3 text-sm text-gray-600 md:mb-4 md:text-base">
              Continued support and networking for program graduates
            </p>
            <ul className="space-y-1.5 text-sm text-gray-600 md:space-y-2 md:text-base">
              <li className="flex items-center">
                <span className="mr-2">•</span>
                Alumni directory & networking
              </li>
              <li className="flex items-center">
                <span className="mr-2">•</span>
                Business update submissions
              </li>
              <li className="flex items-center">
                <span className="mr-2">•</span>
                Webinar invitations
              </li>
              <li className="flex items-center">
                <span className="mr-2">•</span>
                Mentorship opportunities
              </li>
            </ul>
          </motion.div>
        </motion.div>
      </section>

      <section className="bg-gradient-to-b from-gray-50 to-white py-12 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8"
        >
          <div className="rounded-2xl bg-gradient-to-br from-white to-blue-50 p-6 shadow-sm md:p-8">
            <h3 className="mb-4 text-center text-2xl font-semibold text-gray-900 md:mb-6 md:text-3xl">
              Seamless Integration Benefits
            </h3>
            <ul className="grid gap-3 md:grid-cols-2 md:gap-4">
              <li className="flex items-center text-sm text-gray-600 md:text-base">
                <span className="mr-2">•</span>
                Complete lifecycle management from applicant to alumni
              </li>
              <li className="flex items-center text-sm text-gray-600 md:text-base">
                <span className="mr-2">•</span>
                Comprehensive data analytics across all stages
              </li>
              <li className="flex items-center text-sm text-gray-600 md:text-base">
                <span className="mr-2">•</span>
                Alumni re-engagement as mentors and partners
              </li>
              <li className="flex items-center text-sm text-gray-600 md:text-base">
                <span className="mr-2">•</span>
                Long-term impact monitoring and reporting
              </li>
            </ul>
          </div>
        </motion.div>
      </section>

      <section ref={programsRef} className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="px-4 text-center"
        >
          <h2 className="text-3xl font-extrabold tracking-tight text-gray-900 sm:text-5xl md:text-4xl">
            <span className="bg-gradient-to-r from-blue-600 via-teal-500 to-green-500 bg-clip-text text-transparent">
              Entrepreneur
            </span>{' '}
            <span className="text-black">Development</span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-gray-600 md:text-lg">
            Comprehensive Entrepreneurship Development Programs designed to take you from idea to
            successful business.
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mt-12 grid gap-6 md:mt-16 md:grid-cols-3 md:gap-8"
        >
          <motion.div
            variants={cardVariants}
            className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-white to-blue-50 p-6 shadow-sm transition-all hover:scale-[1.02] hover:shadow-lg md:p-8"
          >
            <div className="mb-3 flex justify-center text-3xl md:text-4xl">
              <HiRocketLaunch className="h-10 w-10 text-blue-500" />
            </div>
            <h3 className="mb-3 text-center text-lg font-semibold text-gray-900 md:text-xl">
              Start Your Business
            </h3>
            <p className="mb-3 text-sm text-gray-600 md:mb-4 md:text-base">
              Structure and formalize your business ideas with our comprehensive training program.
            </p>
            <ul className="mb-4 space-y-1.5 text-sm text-gray-600 md:mb-6 md:space-y-2 md:text-base">
              <li className="flex items-center">
                <span className="mr-2">•</span>
                Business Model Canvas
              </li>
              <li className="flex items-center">
                <span className="mr-2">•</span>
                Market Research
              </li>
              <li className="flex items-center">
                <span className="mr-2">•</span>
                Legal Setup
              </li>
              <li className="flex items-center">
                <span className="mr-2">•</span>
                Branding Essentials
              </li>
            </ul>
          </motion.div>

          <motion.div
            variants={cardVariants}
            className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-white to-green-50 p-6 shadow-sm transition-all hover:scale-[1.02] hover:shadow-lg md:p-8"
          >
            <div className="mb-3 flex justify-center text-3xl md:text-4xl">
              <HiChartBar className="h-10 w-10 text-green-500" />
            </div>
            <h3 className="mb-3 text-center text-lg font-semibold text-gray-900 md:text-xl">
              Scale Your Business
            </h3>
            <p className="mb-3 text-sm text-gray-600 md:mb-4 md:text-base">
              One-on-one coaching to help growing businesses reach the next level.
            </p>
            <ul className="mb-4 space-y-1.5 text-sm text-gray-600 md:mb-6 md:space-y-2 md:text-base">
              <li className="flex items-center">
                <span className="mr-2">•</span>
                Growth Strategy
              </li>
              <li className="flex items-center">
                <span className="mr-2">•</span>
                Sales Development
              </li>
              <li className="flex items-center">
                <span className="mr-2">•</span>
                Digital Transformation
              </li>
              <li className="flex items-center">
                <span className="mr-2">•</span>
                Leadership
              </li>
            </ul>
          </motion.div>
          <motion.div
            variants={cardVariants}
            className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-white to-purple-50 p-6 shadow-sm transition-all hover:scale-[1.02] hover:shadow-lg md:p-8"
          >
            <div className="mb-3 flex justify-center text-3xl md:text-4xl">
              <HiHeart className="h-10 w-10 text-purple-500" />
            </div>
            <h3 className="mb-3 text-center text-lg font-semibold text-gray-900 md:text-xl ">
              Keep it Alive
            </h3>
            <p className="mb-3 text-sm text-gray-600 md:mb-4 md:text-base">
              Regular performance assessments to keep your business thriving.
            </p>
            <ul className="mb-4 space-y-1.5 text-sm text-gray-600 md:mb-6 md:space-y-2 md:text-base">
              <li className="flex items-center">
                <span className="mr-2">•</span>
                Health Diagnosis
              </li>
              <li className="flex items-center">
                <span className="mr-2">•</span>
                Risk Alerts
              </li>
              <li className="flex items-center">
                <span className="mr-2">•</span>
                Action Plans
              </li>
              <li className="flex items-center">
                <span className="mr-2">•</span>
                Progress Reports
              </li>
            </ul>
          </motion.div>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
